'use client';

import { createContext, useContext, useMemo, useState, type ReactNode, type JSX } from 'react';

import { translations, type LanguageCode, type Translations } from '../translations';

interface LanguageContextType {
  language: LanguageCode;
  t: Translations;
  setLanguage: (language: LanguageCode) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: Readonly<{ children: ReactNode }>): JSX.Element {
  const [language, setLanguage] = useState<LanguageCode>('sv');

  const value = useMemo(
    () => ({
      language,
      t: translations[language],
      setLanguage,
    }),
    [language],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);

  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
}
